import { useEffect, useState } from "react";

import {
	fetchJobylonPresence,
	type JobylonPresence,
} from "../api/assignments/jobylon-jobs";

/**
 * The Jobylon applications this customer holds for the people on screen.
 *
 * Empty for a customer without the integration, and for everyone Jobylon has
 * never seen — which is what keeps the mark off screens it has nothing to say
 * about, with no caller having to ask whether Jobylon is configured.
 *
 * Failure is deliberately quiet, like {@link useJobylonLinkedJobs}: every
 * caller uses this to decide a mark on a page about something else, so a
 * Jobylon outage must not take that page down.
 *
 * Also empty for a signed-out visitor (the public external review renders
 * person summaries): the endpoint requires auth, and the 401 an anonymous
 * request earns makes the global fetch wrapper (frontend/api/fetch.ts)
 * redirect the whole page to /login?session_expired=true. No token, no
 * request.
 */
export const useJobylonPresence = (personIds: number[]): JobylonPresence => {
	const [presence, setPresence] = useState<JobylonPresence>(new Map());
	// The ids as one value, so a caller re-rendering with a fresh array of the
	// same people does not refetch — this hook is called from list rows.
	const key = personIds.join(",");

	useEffect(() => {
		const ids = key ? key.split(",").map(Number) : [];
		if (ids.length === 0 || !localStorage.getItem("access_token")) {
			setPresence(new Map());
			return;
		}
		const controller = new AbortController();
		fetchJobylonPresence(ids, controller.signal)
			.then(setPresence)
			.catch((err: unknown) => {
				if (err instanceof DOMException && err.name === "AbortError") return;
				console.debug("Jobylon candidates unavailable", err);
			});
		return () => controller.abort();
	}, [key]);

	return presence;
};
